import propsStore from 'client/infra/stores/PropsStore';
import { RuntimeWidget, WidgetType } from 'client/types';
import RuntimeAccordion from 'client/widgets/runtime-accordion/RuntimeAccordion';
import RuntimeAddToCart from 'client/widgets/runtime-add-to-cart';
import runtimeBreadcrumbs from 'client/widgets/runtime-breadcrumbs';
import RuntimeCalBooking from 'client/widgets/runtime-cal-booking/RuntimeCalBooking';
import RuntimeCart from 'client/widgets/runtime-cart';
import RuntimeCollectionSearchWidget from 'client/widgets/runtime-collection-search/RuntimeCollectionSearch';
import RuntimeFilterSort from 'client/widgets/runtime-filter-sort';
import RuntimeFormFileInput from 'client/widgets/runtime-form-file-input';
import RuntimeOptionsVariations from 'client/widgets/runtime-options-variations';
import RuntimeOrderConfirmation from 'client/widgets/runtime-order-confirmation';
import RuntimeProductCustomizations from 'client/widgets/runtime-product-customizations/RuntimeProductCustomizations';
import RuntimeProductPriceOptionsWidget from 'client/widgets/runtime-product-price-options/RuntimeProductPriceOptions';
import RuntimeProductPriceWidget from 'client/widgets/runtime-product-price/RuntimeProductPrice';
import RuntimeSlider from 'client/widgets/runtime-slider';
import { GeneralWidgetProps, WidgetSchema } from 'types';

export interface GenerateComponentArgs extends WidgetSchema {
    observer: boolean;
}

const legacyStoreWidgets = [WidgetType.Cart, WidgetType.Breadcrumbs];

/** hot expects a unique component. Hot is doing some kind of compilation magic. Top Component must be written in a different file (can't come from same HOC). If HOC is used, wrap in a dummy component */
const typeToWidget: Record<WidgetType, RuntimeWidget> = {
    [WidgetType.Slider]: RuntimeSlider,
    [WidgetType.Cart]: RuntimeCart,
    [WidgetType.Breadcrumbs]: runtimeBreadcrumbs,
    [WidgetType.AddToCart]: RuntimeAddToCart,
    [WidgetType.OptionsVariations]: RuntimeOptionsVariations,
    [WidgetType.RuntimeFilterSort]: RuntimeFilterSort,
    [WidgetType.CollectionSearch]: RuntimeCollectionSearchWidget,
    [WidgetType.ProductPrice]: RuntimeProductPriceWidget,
    [WidgetType.ProductPriceOptions]: RuntimeProductPriceOptionsWidget,
    [WidgetType.Accordion]: RuntimeAccordion,
    [WidgetType.CalBooking]: RuntimeCalBooking,
    [WidgetType.ProductCustomizations]: RuntimeProductCustomizations,
    [WidgetType.FormFileInput]: RuntimeFormFileInput,
    [WidgetType.OrderConfirmation]: RuntimeOrderConfirmation,
};

export const getComponentByType = (type: WidgetType): RuntimeWidget => {
    return typeToWidget[type];
};

export const populateWidgetStore = (
    widgetID: string,
    props: GeneralWidgetProps,
    type: WidgetType
) => {
    const widgetStore = propsStore.getWidgetStore(widgetID);
    widgetStore.legacyWidget =
        legacyStoreWidgets.includes(type) || !!widgetStore.model.styles?.rules;
    widgetStore.mergeModel(props);
    widgetStore.setInitialProps(props, widgetID);
};

function endsWith(str: string, suffix: string) {
    return str.indexOf(suffix, str.length - suffix.length) !== -1;
}

export function appendMarkupInsideDiv(
    divMarkup: string,
    newMarkup: string
): string {
    if (endsWith(divMarkup, '</div>')) {
        return divMarkup.replace(/<\/div>$/, newMarkup + '</div>');
    }
    return divMarkup;
}

export function appendMarkupBeforeInsideDiv(
    divMarkup: string,
    newMarkup: string
): string {
    if (endsWith(divMarkup, '</div>')) {
        const match = divMarkup.match(/^<div(.*?)>/) as Array<string>;
        return divMarkup.replace(/^<div(.*?)>/, match[0] + newMarkup);
    }
    return divMarkup;
}

export function shouldHydrateWidgetOnRuntime(widget: WidgetSchema): boolean {
    const Component = getComponentByType(widget.type);
    return !Component?.skipHydration;
}

export function getWrapperId(id: string) {
    return `ssrWrap-${id}`;
}
